import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, BadgeCheck, ChevronLeft, ChevronRight, Clock, Copy, Flame, Gem, HandCoins,
  Heart, PackageCheck, RefreshCcw, ShieldCheck, Sparkles, Truck,
} from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { focalFor, objectPosition } from "@/lib/focal";
import { useStore } from "@/context/StoreContext";
import { inr } from "@/lib/format";
import ProductCard from "@/components/store/ProductCard";
import RateStrip from "@/components/store/RateStrip";
import PromisesBlock from "@/components/store/PromisesBlock";
import StyleQuiz from "@/components/store/StyleQuiz";
import { ShoppableVideos, LookbookHotspots, BundleBuilder, ImageCompare } from "@/components/store/HomeSections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const SILVER_CATS = [
  // Detail frames from the launch-2026 campaign — jewellery is the subject,
  // never a face crop. Keys resolve from admin-editable settings.category_tiles;
  // the Pia contact-sheet frames remain as fallbacks.
  ["Rings", "rings", "tile_rings", "/assets/pia/pia-ring-2.webp"],
  ["Earrings", "earrings", "tile_earrings", "/assets/pia/pia-ear-1.webp"],
  ["Pendants", "pendants", "tile_necklaces", "/assets/pia/pia-editorial-1.webp"],
  ["Necklaces", "necklaces", "tile_necklaces", "/assets/pia/pia-ring-4.webp"],
  ["Bangles", "bangles", "tile_bangles", "/assets/pia/pia-editorial-3.webp"],
  ["Bracelets", "bracelets", "tile_bracelets", null],
  ["Anklets", "anklets", "", null],
  ["Personalised", "", "tile_personalised", null],
];
const COIN_TILES = [
  ["Coins", "coin"], ["Jumbo", "jumbo"], ["Flip Coins", "flip"], ["Notes", "note"],
  ["Sets", "combo"], ["Belpatra", "belpatra"], ["Front & Back", "two-side"], ["Shank", "shank"],
];
const BONDS = [
  // Detail frames from the launch-2026 campaign — no face crops in square tiles.
  ["Wife", "for-wife", "/assets/pia/pia-ring-1.webp"], ["Husband", "for-husband", "/assets/pia/pia-ring-4.webp"],
  ["Mother", "for-mother", "/assets/pia/pia-editorial-2.webp"], ["Father", "for-father", "/assets/pia/pia-editorial-4.webp"],
  ["Sister", "for-sister", "/assets/pia/pia-hd-1.webp"], ["Brother", "for-brother", "/assets/pia/pia-hd-2.webp"],
  ["Friends", "for-friends", "/assets/pia/pia-editorial-1.webp"], ["Daughter", "for-daughter", "/assets/pia/pia-ring-3.webp"],
];
const OCCASIONS = [
  // Lifestyle frames from the launch-2026-lifestyle shoot (occ_* keys), falling
  // back to the launch-2026 detail tiles — consistent with the category strip.
  ["Everyday Wear", "/shop?occasion=Daily%20Wear", "occ_everyday", "tile_bracelets"], ["Office Wear", "/shop?style=stud", "occ_office", "tile_earrings"],
  ["Party Wear", "/shop?occasion=Festive", "occ_festive", "tile_necklaces"], ["Wedding Wear", "/shop?occasion=Wedding", "occ_wedding", "tile_rings"],
  ["Festive", "/shop?occasion=Festive", "occ_festive", "tile_bangles"],
];
const PRICE_BANDS = [
  ["Under ₹10,000", "/gifts/under-10000", null, 10000], ["Under ₹15,000", "/gifts/under-15000", 10000, 15000],
  ["Under ₹20,000", "/gifts/under-20000", 15000, 20000], ["Premium Gifts", "/gifts/premium", 20000, null],
];

/* Price-band tiles show what the money actually buys: the cheapest REAL
 * product in the band, never a lifestyle stand-in. Falls back to the text
 * card until a product in that band has real imagery. */
function PriceBandTile({ label, to, min, max }) {
  const [img, setImg] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams({ sort: "price_asc", limit: "1" });
    if (min) params.set("min_price", min);
    if (max) params.set("max_price", max);
    api.get(`/products?${params}`).then((r) => {
      const items = r.data.items || r.data || [];
      setImg(items[0]?.images?.[0] || null);
    }).catch(() => {});
  }, [min, max]);
  return (
    <Link to={to} className="border border-gold/40 bg-white rounded-xl p-6 text-center hover:border-gold hover:shadow-[0_8px_30px_rgba(201,162,39,0.15)] transition-all group" data-testid={`band-${label.toLowerCase().replace(/[\s₹,]+/g, "-")}`}>
      {img && (
        <div className="h-28 mb-3 rounded-lg overflow-hidden bg-ivory">
          <img src={img} alt={label} loading="lazy" className="h-full w-full object-contain" data-testid={`band-img-${label.toLowerCase().replace(/[\s₹,]+/g, "-")}`} />
        </div>
      )}
      <p className="font-heading text-lg font-semibold text-brand group-hover:text-brand-2">{label}</p>
      <p className="text-xs font-mono text-ink-2 mt-1">LIVE PRICES</p>
    </Link>
  );
}
const HOME_FAQS = [
  ["Are your prices really linked to the live gold rate?",
   "Yes. Every Siyaara price is computed from today's gold and silver rate at the moment you view it — metal value, making charge and GST are shown separately on gold and coins, so you can see exactly what you are paying for."],
  ["Are all prices inclusive of GST?",
   "Yes. The price you see is the price you pay — GST at 3% is already included, never added at checkout."],
  ["How small do your coins really go?",
   "Our handcrafted 999 fine coins start at just 25 mg (0.025 g) — among the smallest certified gold coins made in India — and go up through 1 GM, 2.5 GM and 5 GM jumbos. Every coin carries a QR-verifiable certificate."],
  ["What is your return and buyback policy?",
   "Returns within 2 days of delivery and exchange within 10 days on unworn jewellery in original packaging (personalised pieces are made to order and non-returnable). Lifetime exchange on all Siyaara pieces; lifetime buyback on gold at the prevailing rate."],
];

function useProducts(params, deps = []) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let stale = false; // guard: a slow previous request must not overwrite a newer one
    setLoaded(false);
    api.get("/products", { params: { limit: 12, ...params } })
      .then((r) => { if (!stale) setItems(r.data.items); })
      .catch(() => { if (!stale) setItems([]); })
      .finally(() => { if (!stale) setLoaded(true); });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { items, loaded };
}

function Rail({ items, loaded, emptyText = "New pieces are on their way." }) {
  if (!loaded) {
    return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">{Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="rounded-xl border border-brand/10 bg-white animate-pulse"><div className="aspect-square bg-brand-light" /><div className="p-4 space-y-2"><div className="h-3 bg-brand-light w-2/3" /><div className="h-3 bg-brand-light w-1/3" /></div></div>
    ))}</div>;
  }
  if (items.length === 0) return null; // integrity rule: never render placeholder rails
  return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">{items.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>;
}

function SectionHead({ eyebrow, title, link, linkLabel = "View all" }) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div>
        <p className="eyebrow !text-gold">{eyebrow}</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1.5 text-ink">{title}</h2>
      </div>
      {link && <Link to={link} className="text-sm text-brand underline underline-offset-4 decoration-gold hover:text-brand-2 transition-colors shrink-0" data-testid={`section-link-${title.toLowerCase().replace(/\s+/g, "-")}`}>{linkLabel}</Link>}
    </div>
  );
}

/* ---------------- Hero carousel ----------------
   Portrait assets are never centre-cropped into the wide slot: they render in
   a split layout — image at natural aspect on one side, headline on the other.
   Full-bleed slides anchor every responsive crop to the slide's focal point
   (object-position) and the text block shifts to the side opposite the focal
   point so a headline never sits across a face. */
function Hero({ slides, config }) {
  const [idx, setIdx] = useState(0);
  const nav = useNavigate();
  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);
  if (!slides.length) return null;
  const s = slides[idx % slides.length];
  const focal = s.focal || focalFor(s.image, config);
  const textLeft = s.text_side && s.text_side !== "auto" ? s.text_side === "left" : focal.x >= 55;
  // Structured copy fields (admin-editable): eyebrow, alignment, colour, scrim.
  // Text sits on the empty side of the image, opposite the focal point.
  const align = s.text_align || "left";
  const alignCls = align === "centre" ? "items-center text-center"
    : align === "right" ? "items-end text-right"
    : textLeft ? "items-start text-left" : "items-end text-right";
  const darkText = s.text_colour === "dark";
  const scrim = s.scrim ?? 55;

  const arrows = slides.length > 1 && (
    <div className="absolute bottom-5 right-6 z-10 flex gap-2">
      <button onClick={() => setIdx((idx - 1 + slides.length) % slides.length)} className="h-9 w-9 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/30 transition-colors" aria-label="Previous" data-testid="hero-prev"><ChevronLeft size={17} /></button>
      <button onClick={() => setIdx((idx + 1) % slides.length)} className="h-9 w-9 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/30 transition-colors" aria-label="Next" data-testid="hero-next"><ChevronRight size={17} /></button>
    </div>
  );

  const copy = (
    <div className={`flex flex-col ${alignCls}`}>
      {s.eyebrow && <p className={`${darkText ? "text-brand" : "text-gold"} text-xs font-medium uppercase tracking-[0.3em] mb-3`} data-testid="hero-eyebrow">{s.eyebrow}</p>}
      <h1 className={`font-heading text-4xl sm:text-6xl font-semibold ${darkText ? "text-ink" : "text-white"} max-w-2xl leading-[1.05]`} data-testid="hero-headline">{s.headline}</h1>
      {s.subtext && <p className={`${darkText ? "text-ink-2" : "text-white/80"} mt-3 max-w-xl text-sm sm:text-base`} data-testid="hero-subtext">{s.subtext}</p>}
      <Button onClick={() => nav(s.link || "/shop")}
        className="mt-6 bg-brand hover:bg-brand-2 text-ivory rounded-full px-8 h-11 border border-gold/60 btn-press"
        data-testid="hero-cta">{s.cta || "Shop now"}</Button>
    </div>
  );

  if (!s.video && s.layout === "split") {
    return (
      <section className="relative bg-brand overflow-hidden" data-testid="hero-carousel">
        {/* Mobile: tall asset full-bleed, copy in the lower empty area (dark text on the light zone) */}
        <div className="relative md:hidden">
          <picture>
            {s.mobile_image && <source media="(max-width: 767px)" srcSet={s.mobile_image} />}
            <img key={s.image} src={s.image} alt={s.headline} data-testid="hero-image" className="w-full h-auto" />
          </picture>
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-white/95 via-white/70 to-transparent px-6 pb-7 pt-20">
            {s.eyebrow && <p className="text-brand text-[10px] font-medium uppercase tracking-[0.3em] mb-2" data-testid="hero-eyebrow">{s.eyebrow}</p>}
            <h1 className="font-heading text-3xl font-semibold text-ink leading-[1.08]" data-testid="hero-headline">{s.headline}</h1>
            {s.subtext && <p className="text-ink-2 mt-2 text-sm" data-testid="hero-subtext">{s.subtext}</p>}
            <Button onClick={() => nav(s.link || "/shop")}
              className="mt-4 bg-brand hover:bg-brand-2 text-ivory rounded-full px-7 h-10 border border-gold/60 btn-press"
              data-testid="hero-cta">{s.cta || "Shop now"}</Button>
          </div>
        </div>
        {/* Desktop: split — image keeps its natural aspect, copy on the empty side */}
        <div className="hidden md:grid md:grid-cols-[3fr_2fr] items-stretch">
          <div className={`flex items-center ${textLeft ? "order-1" : "order-2"}`}>
            <div className="container-x py-14 md:py-20">{copy}</div>
          </div>
          <div className={`relative bg-brand-light/30 flex items-center justify-center md:h-[70vh] md:min-h-[420px] md:max-h-[680px] ${textLeft ? "order-2" : "order-1"}`}>
            <img key={s.image} src={s.image} alt={s.headline} data-testid="hero-image-desktop"
              className="h-full w-auto max-w-full object-contain" />
          </div>
        </div>
        {arrows}
        <div className="absolute bottom-0 inset-x-0 h-px bg-gold/60" />
      </section>
    );
  }

  return (
    <section className="relative bg-brand overflow-hidden" data-testid="hero-carousel">
      <div className="relative h-[62vh] min-h-[420px] max-h-[640px]">
        {s.video ? (
          <video key={s.video} className="absolute inset-0 h-full w-full object-cover" src={s.video}
            poster={s.poster || s.image} autoPlay muted loop playsInline data-testid="hero-video" />
        ) : (
          <picture>
            {s.mobile_image && <source media="(max-width: 767px)" srcSet={s.mobile_image} />}
            <img key={s.image} src={s.image} alt={s.headline} data-testid="hero-image"
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: `${focal.x}% ${focal.y}%` }} />
          </picture>
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, rgba(0,0,0,${scrim / 100}), rgba(0,0,0,${scrim / 300}), transparent)` }} data-testid="hero-scrim" />
        <div className="absolute inset-x-0 bottom-0 container-x pb-14">{copy}</div>
        {arrows}
      </div>
      <div className="absolute bottom-0 inset-x-0 h-px bg-gold/60" />
    </section>
  );
}

/** Section headings come from admin homepage settings; fallbacks are Siyaara copy. */
function secTitle(config, id, fallback) {
  const s = (config?.homepage?.sections || []).find((x) => x.id === id);
  return s?.title || fallback;
}

/* ---------------- Category scroller ---------------- */
function CategoryScroller({ store }) {
  const ref = useRef(null);
  const { collections, config } = useStore();
  const tiles = store === "coins"
    ? COIN_TILES.map(([label, fam]) => ({ label, to: `/shop?product_type=coin&family=${fam}`, img: fam === "coin" ? config?.category_tiles?.tile_coins || null : null, tagline: (config?.category_taglines || {})[label] || "" }))
    : SILVER_CATS.map(([label, slug, key, fallback]) => {
        const col = collections.find((c) => c.slug === slug);
        const to = slug ? `/shop?metal=${store}&category=${encodeURIComponent(label)}` : "/shop?personalised=true";
        return { label, to, img: (key && config?.category_tiles?.[key]) || fallback || col?.image || null,
                 tagline: (config?.category_taglines || {})[label] || col?.tagline || "" };
      });
  return (
    <section className="container-x mt-14" data-testid="shop-by-category">
      <SectionHead eyebrow="THE COLLECTIONS" title={secTitle(config, "categories", "Find your piece")} link="/shop" />
      <div className="relative mt-6">
        <div ref={ref} className="flex gap-5 overflow-x-auto pb-2 snap-x scrollbar-none" style={{ scrollbarWidth: "none" }}>
          {tiles.map((t) => (
            <Link key={t.label} to={t.to} className="snap-start shrink-0 w-28 sm:w-32 text-center group" data-testid={`cat-tile-${t.label.toLowerCase().replace(/[\s&]+/g, "-")}`}>
              <div className="h-28 w-28 sm:h-32 sm:w-32 rounded-full overflow-hidden border border-brand/15 bg-white mx-auto group-hover:border-gold transition-colors">
                {t.img
                  ? <img src={t.img} alt={t.label} loading="lazy" style={{ objectPosition: objectPosition(t.img, config) }} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  : <div className="h-full w-full flex items-center justify-center bg-brand-light"><Gem size={22} className="text-brand-3" /></div>}
              </div>
              <p className="text-sm mt-2.5 font-medium text-ink group-hover:text-brand transition-colors">{t.label}</p>
              {t.tagline && <p className="text-[11px] leading-snug text-stone-500 mt-0.5 px-1" data-testid={`cat-tagline-${t.label.toLowerCase().replace(/[\s&]+/g, "-")}`}>{t.tagline}</p>}
            </Link>
          ))}
        </div>
        <button onClick={() => ref.current?.scrollBy({ left: -300, behavior: "smooth" })} className="hidden sm:flex absolute -left-4 top-12 h-9 w-9 rounded-full bg-white border border-brand/15 items-center justify-center shadow-sm hover:border-gold" aria-label="Scroll left" data-testid="cat-scroll-left"><ArrowLeft size={15} /></button>
        <button onClick={() => ref.current?.scrollBy({ left: 300, behavior: "smooth" })} className="hidden sm:flex absolute -right-4 top-12 h-9 w-9 rounded-full bg-white border border-brand/15 items-center justify-center shadow-sm hover:border-gold" aria-label="Scroll right" data-testid="cat-scroll-right"><ArrowRight size={15} /></button>
      </div>
    </section>
  );
}

/* ---------------- Bestsellers w/ category tabs ---------------- */
function Bestsellers({ store }) {
  const { config } = useStore();
  const tabs = store === "coins" ? ["coin", "flip", "note", "combo"] : ["Rings", "Pendants", "Necklaces", "Earrings"];
  const [tab, setTab] = useState(tabs[0]);
  const params = store === "coins" ? { product_type: "coin", family: tab } : { metal: store, product_type: "jewellery", category: tab };
  const { items, loaded } = useProducts(params, [store, tab]);
  return (
    <section className="container-x mt-16" data-testid="bestsellers">
      <SectionHead eyebrow="MOST LOVED" title={secTitle(config, "bestsellers", "Most loved")} link="/shop" />
      <div className="flex gap-2 mt-5 flex-wrap">
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors btn-press ${tab === t ? "bg-brand text-ivory border-brand" : "border-brand/20 text-ink-2 hover:border-brand"}`}
            data-testid={`bestseller-tab-${t.toLowerCase()}`}>
            {t === "coin" ? "Coins" : t === "combo" ? "Sets" : t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>
      <Rail items={items} loaded={loaded} />
    </section>
  );
}

/* ---------------- Coins feature block ---------------- */
function CoinBlock() {
  const { config } = useStore();
  const [rate, setRate] = useState(null);
  const { items } = useProducts({ product_type: "coin", metal: "gold" }, []);
  useEffect(() => { api.get("/rates/current").then((r) => setRate(r.data)).catch(() => {}); }, []);
  const goldCoins = items.find((p) => p.slug === "gold-coins-999");
  const picks = goldCoins ? [0, 2, 6, 7].map((i) => goldCoins.variants[i]).filter(Boolean) : [];
  const banner = config?.brand_imagery?.coins_banner;
  const ink = banner ? "text-brand" : "";
  const sub = banner ? "text-brand/75" : "text-ivory/70";
  const dim = banner ? "text-brand/60" : "text-ivory/60";
  return (
    <section className="mt-16 relative overflow-hidden bg-brand text-ivory" data-testid="coins-block">
      {banner && (
        <>
          <img src={banner} alt="Handcrafted 999 fine gold coins" data-testid="coins-banner-image"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: objectPosition(banner, config) }} />
          <div className="absolute inset-0 bg-gradient-to-r from-ivory via-ivory/85 to-transparent" />
        </>
      )}
      <div className="container-x relative py-14 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="eyebrow !text-gold">999 FINE · ASSAY CERTIFIED</p>
          <h2 className={`font-heading text-3xl sm:text-4xl font-semibold mt-2 ${ink}`}>{secTitle(config, "coins", "Gold and silver coins, handcrafted")}</h2>
          <p className={`${sub} mt-3 text-sm leading-relaxed max-w-md`}>
            53 auspicious designs — deities, symbols and occasions — struck on 999 fine gold and silver.
            Every coin ships in a tamper-proof blister with a QR-verifiable Certificate of Authenticity.
          </p>
          {rate && (
            <p className={`mt-4 text-sm font-mono ${dim}`} data-testid="coins-block-rate">
              Today's rate: <span className="gold-num font-semibold">₹{Number(rate.gold_24k).toLocaleString("en-IN")}/g</span> gold · <span className="gold-num font-semibold">₹{Number(rate.silver).toLocaleString("en-IN")}/g</span> silver
            </p>
          )}
          <div className="flex gap-3 mt-6">
            <Link to="/coins"><Button className="bg-gold text-brand hover:bg-gold-light rounded-full px-7 btn-press font-semibold" data-testid="coins-block-cta">Shop Coins</Button></Link>
            <Link to="/verify"><Button variant="outline" className={`rounded-full px-7 btn-press ${banner ? "border-brand/40 text-brand hover:bg-brand/5" : "border-ivory/40 text-ivory hover:bg-white/10"}`} data-testid="coins-verify-cta">Verify a Certificate</Button></Link>
          </div>
        </div>
        {!banner && (
        <div className="grid grid-cols-2 gap-3" data-testid="coins-popular-weights">
          {picks.map((v) => (
            <Link key={v.id} to="/products/gold-coins-999" className="bg-white/5 border border-gold/25 rounded-xl p-4 hover:bg-white/10 transition-colors" data-testid={`coin-weight-${v.id}`}>
              <p className="font-heading text-lg font-semibold text-gold">{v.options?.Weight}</p>
              <p className="text-ivory/70 text-sm mt-1">{inr(v.pricing?.display_price ?? v.pricing?.final ?? 0)}</p>
            </Link>
          ))}
        </div>
        )}
      </div>
    </section>
  );
}

/* ---------------- Personalised widget ---------------- */
function PersonalisedBlock() {
  const { config } = useStore();
  const [name, setName] = useState("Aarav");
  return (
    <section className="container-x mt-16" data-testid="personalised-block">
      <div className="grid lg:grid-cols-2 gap-10 items-center bg-white border border-brand/10 rounded-2xl p-8 sm:p-12">
        <div>
          <p className="eyebrow !text-gold">MADE TO ORDER</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold mt-2">{secTitle(config, "personalised", "Make it theirs")}</h2>
          <p className="text-ink-2 mt-3 text-sm leading-relaxed max-w-md">
            Name pendants, engraved rings and date bands — preview your engraving live, then we handcraft it in 7–10 days.
          </p>
          <div className="mt-5 max-w-xs">
            <Input value={name} maxLength={10} onChange={(e) => setName(e.target.value.replace(/[^a-zA-Z\s]/g, ""))}
              placeholder="Type a name" className="bg-ivory border-brand/20" data-testid="engrave-preview-input" />
          </div>
          <Link to="/shop?personalised=true">
            <Button className="mt-5 bg-brand hover:bg-brand-2 text-ivory rounded-full px-7 btn-press" data-testid="personalised-cta">Start Personalising</Button>
          </Link>
        </div>
        <div className="relative flex items-center justify-center">
          <div className="w-64 h-40 rounded-2xl bg-gradient-to-br from-gold-light to-gold shadow-inner flex items-center justify-center" data-testid="engrave-preview">
            <span className="font-script text-5xl text-brand/80">{name || "Your name"}</span>
          </div>
          <p className="absolute -bottom-3 right-6 text-[10px] font-mono text-ink-2 bg-white border border-brand/10 rounded-full px-3 py-1">LIVE PREVIEW</p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Latest collections (editorial cards) ---------------- */
function LatestCollections() {
  const { collections, config } = useStore();
  const [thumbs, setThumbs] = useState({});
  useEffect(() => {
    collections.slice(0, 3).forEach((c) => {
      api.get("/products", { params: { collection: c.slug, limit: 3 } })
        .then((r) => setThumbs((t) => ({ ...t, [c.slug]: r.data.items })))
        .catch(() => {});
    });
  }, [collections]);
  const cols = collections.slice(0, 3);
  if (!cols.length) return null;
  return (
    <section className="container-x mt-16" data-testid="latest-collections">
      <SectionHead eyebrow="EDITORIAL" title={secTitle(config, "editorial", "The Siyaara edit")} link="/shop" />
      <div className="grid md:grid-cols-3 gap-6 mt-6">
        {cols.map((c) => (
          <Link key={c.slug} to={`/collections/${c.slug}`} className="group" data-testid={`collection-card-${c.slug}`}>
            <div className="rounded-2xl overflow-hidden border border-brand/10 aspect-[4/3] bg-white">
              {c.image
                ? <img src={c.image} alt={c.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                : <div className="h-full w-full bg-brand-light flex items-center justify-center"><Gem className="text-brand-3" /></div>}
            </div>
            <p className="font-heading text-xl font-semibold mt-3 group-hover:text-brand transition-colors">{c.name}</p>
            <div className="flex gap-2 mt-2">
              {(thumbs[c.slug] || []).map((p) => (
                <img key={p.id} src={(p.images || [])[0]} alt={p.title} loading="lazy" className="h-14 w-14 rounded-lg object-cover border border-brand/10 bg-white" />
              ))}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Testimonials + Instagram ---------------- */
function SocialProof() {
  const { config } = useStore();
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => { api.get("/testimonials").then((r) => setTestimonials(r.data || [])).catch(() => {}); }, []);
  if (!testimonials.length) return null; // real records only — hidden until admin adds them
  return (
    <section className="container-x mt-16" data-testid="testimonials">
      <SectionHead eyebrow="WORD OF MOUTH" title={secTitle(config, "reviews", "From our customers")} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {testimonials.slice(0, 4).map((t) => (
          <figure key={t.id} className="bg-white border border-brand/10 rounded-xl p-5" data-testid={`testimonial-${t.id}`}>
            {t.photo && <img src={t.photo} alt="" className="h-28 w-full object-cover rounded-lg mb-3" />}
            <div className="flex gap-0.5 text-gold">{"★".repeat(t.rating)}{"☆".repeat(5 - t.rating)}</div>
            <blockquote className="text-sm text-ink mt-2 leading-relaxed line-clamp-4">“{t.text}”</blockquote>
            <figcaption className="text-xs font-mono text-ink-2 mt-3">{t.name}{t.location ? ` · ${t.location}` : ""}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function InstagramStrip() {
  const { config } = useStore();
  const frames = [1, 2, 3, 4].map((i) => `/assets/pia/pia-hd-${i}.webp`);
  return (
    <section className="container-x mt-16" data-testid="instagram-strip">
      <SectionHead eyebrow="@SIYAARA" title={secTitle(config, "gallery", "@siyaara")} link="/pia" linkLabel="Meet Pia" />
      <div className="grid grid-cols-4 gap-3 mt-6">
        {frames.map((f) => (
          <Link key={f} to="/pia" className="rounded-xl overflow-hidden border border-brand/10 aspect-[3/4] bg-white group">
            <img src={f} alt="Siyaara campaign" loading="lazy" style={{ objectPosition: objectPosition(f, config) }} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Newsletter ---------------- */
function Newsletter() {
  const [contact, setContact] = useState("");
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(null);
  const { config } = useStore();
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/newsletter", { contact, channel: contact.includes("@") ? "email" : "whatsapp", consent });
      setDone(r.data);
      if (r.data.code) toast.success(`Your code: ${r.data.code}`);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="container-x mt-16" data-testid="newsletter">
      <div className="bg-brand rounded-2xl text-center px-6 py-14 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-gold/60" />
        <p className="eyebrow !text-gold">FIRST-ORDER TREAT</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-ivory mt-2">
          Get {config?.newsletter_offer_pct || 10}% off your first order
        </h2>
        <p className="text-ivory/60 text-sm mt-2">Join the list — your personal code arrives instantly. WhatsApp or email, your call.</p>
        {done ? (
          <div className="mt-6" data-testid="newsletter-success">
            {done.code ? (
              <p className="text-ivory">Your code: <span className="font-mono font-semibold text-gold text-lg tracking-widest" data-testid="newsletter-code">{done.code}</span> — apply it at checkout.</p>
            ) : (
              <p className="text-ivory">You're on the list already — welcome back.</p>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 max-w-md mx-auto">
            <div className="flex gap-2">
              <Input value={contact} onChange={(e) => setContact(e.target.value)} required
                placeholder="Email or WhatsApp number" className="bg-white/10 border-ivory/25 text-ivory placeholder:text-ivory/40 h-11 rounded-full" data-testid="newsletter-input" />
              <Button type="submit" className="bg-gold text-brand hover:bg-gold-light rounded-full px-6 h-11 btn-press font-semibold" data-testid="newsletter-submit">Join</Button>
            </div>
            <label className="flex items-start gap-2 mt-3 text-left text-xs text-ivory/70">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required
                className="mt-0.5 accent-[#C9A227]" data-testid="newsletter-consent" />
              <span>Yes — email/WhatsApp me launch news and offers. I can opt out any time.</span>
            </label>
          </form>
        )}
      </div>
    </section>
  );
}

/* ---------------- Festival calendar ribbon ----------------
   Static Indian jewellery-calendar dates; the next festival within 45 days
   earns a slim gold ribbon under the hero with a live day countdown. */
const FESTIVALS = [
  ["Makar Sankranti", 1, 14, "New beginnings in gold"],
  ["Valentine's Day", 2, 14, "Say it with something lasting"],
  ["Holi", 3, 4, "Colour, meet gold"],
  ["Akshaya Tritiya", 4, 20, "The most auspicious day to buy gold"],
  ["Raksha Bandhan", 8, 28, "For the one who always showed up"],
  ["Ganesh Chaturthi", 9, 14, "Blessings, struck in 999 fine"],
  ["Navratri", 10, 11, "Nine nights, nine looks"],
  ["Dussehra", 10, 20, "Victory, celebrated in gold"],
  ["Karva Chauth", 10, 30, "For her, from him"],
  ["Dhanteras", 11, 7, "Bring home prosperity"],
  ["Diwali", 11, 8, "The festival of lights — and gold"],
  ["Bhai Dooj", 11, 11, "The sibling bond, sealed"],
];

function FestivalRibbon() {
  const now = new Date();
  const upcoming = FESTIVALS.map(([name, m, d, line]) => {
    let date = new Date(now.getFullYear(), m - 1, d);
    if (date < now) date = new Date(now.getFullYear() + 1, m - 1, d);
    return { name, line, days: Math.round((date - now) / 86400000) };
  }).sort((a, b) => a.days - b.days)[0];
  if (!upcoming || upcoming.days > 45) return null;
  return (
    <Link to="/gifts" className="block bg-gradient-to-r from-brand via-brand-2 to-brand border-y border-gold/50" data-testid="festival-ribbon">
      <div className="container-x py-2.5 flex items-center justify-center gap-3 text-center">
        <Sparkles size={14} className="text-gold shrink-0" />
        <p className="text-ivory text-xs sm:text-sm">
          <span className="text-gold font-medium">{upcoming.name}</span>
          <span className="mx-2 text-ivory/40">·</span>
          <span className="text-ivory/80">{upcoming.line}</span>
          <span className="mx-2 text-ivory/40">·</span>
          <span className="font-mono text-gold">{upcoming.days === 0 ? "TODAY" : `${upcoming.days}D TO GO`}</span>
        </p>
      </div>
    </Link>
  );
}

/* ---------------- Deal of the Day ----------------
   The product where today's best active coupon bites deepest, with a
   countdown to midnight — computed from live offers, never hardcoded. */
function DealOfTheDay({ store }) {
  const { offers } = useStore();
  const params = store === "coins" ? { product_type: "coin" } : { metal: store, product_type: "jewellery" };
  const { items, loaded } = useProducts(params, [store]);
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date(now); end.setHours(23, 59, 59, 999);
      const s = Math.max(0, Math.floor((end - now) / 1000));
      setLeft(`${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);
  if (!loaded || !items.length || !offers?.length)
    return <div data-testid="deal-debug-null" data-loaded={String(loaded)} data-items={items.length} data-offers={offers?.length ?? -1} className="hidden" />;
  let best = null;
  for (const p of items) {
    const price = p.price_min ?? 0;
    if (!price) continue;
    for (const o of offers) {
      if (o.min_order && price < o.min_order) continue;
      const disc = o.type === "percentage" ? price * o.value / 100 : o.type === "flat" ? Math.min(o.value, price) : 0;
      if (disc > 0 && (!best || disc > best.disc)) best = { p, o, disc };
    }
  }
  if (!best) return <div data-testid="deal-debug-nobest" className="hidden" />;
  const { p, o, disc } = best;
  return (
    <section className="container-x mt-16" data-testid="deal-of-the-day">
      <div className="grid lg:grid-cols-2 border border-gold/40 rounded-2xl overflow-hidden bg-white">
        <Link to={`/products/${p.slug}`} className="relative aspect-square lg:aspect-auto bg-ivory block" data-testid="deal-image-link">
          <img src={(p.images || [])[0]} alt={p.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          <span className="absolute top-4 left-4 bg-gold text-brand text-[10px] font-mono tracking-widest px-3 py-1 rounded-full">DEAL OF THE DAY</span>
        </Link>
        <div className="p-8 sm:p-12 flex flex-col justify-center">
          <p className="eyebrow !text-gold">TODAY ONLY</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 text-ink">{p.title}</h2>
          <p className="text-sm text-ink-2 mt-3">
            Use code <span className="font-mono font-semibold text-brand">{o.code}</span> and save{" "}
            <span className="gold-num font-semibold">{inr(disc)}</span> —{" "}
            {o.type === "percentage" ? `${o.value}% off` : `${inr(o.value)} off`} at checkout.
          </p>
          <p className="mt-5 flex items-center gap-2 text-sm font-mono text-brand" data-testid="deal-countdown">
            <Clock size={15} className="text-gold" /> ENDS IN <span className="gold-num font-semibold text-base">{left}</span>
          </p>
          <div className="flex gap-3 mt-6">
            <Link to={`/products/${p.slug}`}><Button className="bg-brand hover:bg-brand-2 text-ivory rounded-full px-7 btn-press" data-testid="deal-cta">Shop the Deal</Button></Link>
            <Button variant="outline" className="rounded-full px-7 btn-press border-brand/25"
              onClick={() => { navigator.clipboard?.writeText(o.code); toast.success(`Code ${o.code} copied`); }}
              data-testid="deal-copy-code"><Copy size={14} className="mr-1.5" /> {o.code}</Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Tiered coupon cards ---------------- */
function OfferCards() {
  const { offers, config } = useStore();
  if (!offers?.length) return null;
  const label = (o) => o.type === "percentage" ? `${o.value}% OFF` : o.type === "flat" ? `${inr(o.value)} OFF` : "FREE SHIPPING";
  return (
    <section className="container-x mt-16" data-testid="offer-cards">
      <SectionHead eyebrow="UNLOCK MORE" title={secTitle(config, "offers", "Offers, stacked in your favour")} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {offers.slice(0, 4).map((o) => (
          <div key={o.code} className="border border-dashed border-gold/60 rounded-xl p-5 bg-white hover:border-gold transition-colors" data-testid={`offer-card-${o.code}`}>
            <p className="font-heading text-2xl font-semibold text-brand">{label(o)}</p>
            {o.min_order > 0 && <p className="text-[11px] font-mono text-ink-2 mt-1">ON ORDERS ABOVE {inr(o.min_order)}</p>}
            <div className="flex items-center justify-between mt-4 border-t border-brand/10 pt-3">
              <span className="font-mono text-sm font-semibold tracking-widest text-ink">{o.code}</span>
              <button onClick={() => { navigator.clipboard?.writeText(o.code); toast.success(`Code ${o.code} copied`); }}
                className="text-brand hover:text-brand-2 transition-colors" aria-label={`Copy ${o.code}`} data-testid={`offer-copy-${o.code}`}>
                <Copy size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Shop by Colour ---------------- */
function ShopByColour({ store }) {
  if (store === "coins") return null;
  const tiles = store === "silver"
    ? [["Silver", "Silver", "linear-gradient(135deg,#F4F4F4,#D9D9D9)"], ["Oxidised", "Oxidised", "linear-gradient(135deg,#C8C4BC,#8F8A80)"]]
    : [["Yellow", "Yellow", "linear-gradient(135deg,#F3DFA0,#D4AF37)"], ["Rose", "Rose", "linear-gradient(135deg,#F2C9B8,#D89B85)"], ["White", "White", "linear-gradient(135deg,#F4F4F4,#C9C9C9)"]];
  return (
    <section className="container-x mt-16" data-testid="shop-by-colour">
      <SectionHead eyebrow="YOUR SHADE" title="Shop by colour" />
      <div className={`grid ${tiles.length === 2 ? "grid-cols-2 max-w-xl" : "grid-cols-3"} gap-4 mt-6`}>
        {tiles.map(([label, colour, bg]) => (
          <Link key={label} to={`/shop?metal=${store}&colour=${colour}`}
            className="rounded-2xl h-32 sm:h-40 relative overflow-hidden group border border-brand/10" style={{ background: bg }}
            data-testid={`colour-${label.toLowerCase()}`}>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-heading text-xl sm:text-2xl font-semibold text-brand">{label}</span>
              <span className="text-[10px] font-mono tracking-widest text-brand/70 mt-1 group-hover:tracking-[0.25em] transition-all">SHOP {label.toUpperCase()}</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Heritage band ---------------- */
function HeritageBand() {
  return (
    <section className="mt-16 bg-brand text-ivory relative overflow-hidden" data-testid="heritage-band">
      <div className="absolute inset-x-0 top-0 h-px bg-gold/60" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gold/60" />
      <div className="container-x py-16 grid lg:grid-cols-[2fr_3fr] gap-10 items-center">
        <div>
          <p className="eyebrow !text-gold">THE SIYAARA WAY</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 leading-tight">Honest gold, priced by the gram — not by the tag.</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-6 text-sm">
          {[
            ["Live-rate pricing", "Every price recomputes from today's gold & silver rate. Metal, making and GST — shown separately."],
            ["Made to order", "Crafted after you order, in 7–10 days. No old stock, no inventory games — your piece is yours alone."],
            ["Lifetime buyback", "Exchange or sell back any Siyaara piece at the prevailing rate. Gold should never lose its word."],
          ].map(([t, d]) => (
            <div key={t} className="border-l border-gold/40 pl-4">
              <p className="font-heading font-semibold text-gold">{t}</p>
              <p className="text-ivory/70 mt-1.5 leading-relaxed text-[13px]">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Home ---------------- */
export default function Home() {
  const { config, store, storeDef } = useStore();

  useEffect(() => {
    document.title = storeDef.title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = storeDef.meta;
  }, [storeDef]);

  const hp = config?.homepage || {};
  // Section gating: admin homepage settings can hide a section by id (with
  // aliases); anything not listed stays visible by default.
  const SECTION_ALIASES = { trustbar: "trustbar", testimonials: "reviews", newsletter: "newsletter" };
  const sectionList = hp.sections || [];
  const showSection = (id) => {
    const key = SECTION_ALIASES[id] || id;
    const found = sectionList.find((s) => s.id === key || s.id === id);
    return found ? found.enabled !== false : true;
  };
  const slides = (hp.slides || []).filter((s) => !s.store || s.store === store);
  const allSlides = slides.length ? slides : (hp.slides || []);

  const shopParams = store === "coins" ? { product_type: "coin" } : { metal: store, product_type: "jewellery" };
  const bestsellers = useProducts({ ...shopParams }, [store]);
  const newArrivals = useProducts({ ...shopParams, sort: "newest" }, [store]);

  // Merchandising blocks (shoppable videos, lookbook, bundle builder, image
  // compare) resolved server-side with live pricing; null blocks never render.
  const [hpSections, setHpSections] = useState(null);
  useEffect(() => {
    api.get("/homepage/sections").then((r) => setHpSections(r.data)).catch(() => {});
  }, []);

  const bisRegistered = Boolean(config?.legal?.bis_reg);
  const trust = [
    [ShieldCheck, "BIS Hallmarked", "bis"],
    [BadgeCheck, "Certified Purity"],
    [RefreshCcw, "2-Day Returns · 10-Day Exchange"], [Truck, "Free Insured Shipping"], [HandCoins, "Lifetime Exchange"],
  ].filter((t) => t[2] !== "bis" || bisRegistered); // compliance: no hallmark claim until BIS reg is entered

  return (
    <div data-testid={`home-${store}`}>
      {/* 5 — Hero carousel */}
      <Hero slides={allSlides} config={config} />

      {/* 5b — Festival calendar ribbon (next jewellery festival ≤ 45 days out) */}
      <FestivalRibbon />

      {/* Live rate placements (admin-tunable): coins = feature block near the
          top where rate transparency sells; gold = slim band below the hero */}
      {(config?.rate_strip_locations || ["footer", "coins_home", "gold_home", "product_breakup"]).includes("coins_home") && store === "coins" && <RateStrip mode="feature" />}
      {(config?.rate_strip_locations || ["footer", "coins_home", "gold_home", "product_breakup"]).includes("gold_home") && store === "gold" && <RateStrip mode="band" />}

      {/* 6 — Shop by Category */}
      {showSection("categories") && <CategoryScroller store={store} />}

      {/* 6b — Shop by Colour (metal finish tiles) */}
      <ShopByColour store={store} />

      {/* 6c — The Siyaara promise (settings.brand_promises — only committed truths render) */}
      <PromisesBlock />

      {/* 6d — Old gold exchange estimator (lead tool, not a transaction) */}
      <section className="container-x mt-14" data-testid="old-gold-band">
        <Link to="/old-gold-exchange" className="block border border-gold/40 bg-brand text-ivory p-6 sm:p-8 hover:border-gold transition-colors" data-testid="old-gold-band-link">
          <p className="eyebrow !text-gold">OLD GOLD EXCHANGE</p>
          <p className="font-heading text-2xl font-semibold mt-1">What is your old gold worth today?</p>
          <p className="text-sm text-ivory/70 mt-1">An instant indicative figure from the live rate — final value only after physical purity &amp; weight testing. No obligation.</p>
        </Link>
      </section>

      {/* 7 — Brand promise strip */}
      {showSection("trustbar") && (
        <section className="border-y border-brand/10 bg-white mt-14" data-testid="trust-strip">
          <div className="container-x py-5 flex flex-wrap justify-center gap-x-10 gap-y-3">
            {trust.map(([Icon, label]) => (
              <span key={label} className="inline-flex items-center gap-2 text-sm text-ink-2">
                <Icon size={17} className="text-gold" /> {label}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* 9 — Gifts for Him / Her: split cards — detail frames anchored by focal, never a face crop */}
      <section className="container-x mt-16 grid sm:grid-cols-2 gap-5" data-testid="gifts-split">
        {[["Gifts for Her", "/gifts/for-wife", config?.brand_imagery?.gift_her || "/assets/pia/pia-ring-2.webp"], ["Gifts for Him", "/gifts/for-husband", "/assets/pia/pia-editorial-3.webp"]].map(([label, to, img]) => (
          <Link key={label} to={to} className="rounded-2xl overflow-hidden h-52 group border border-brand/10 bg-brand flex" data-testid={`gifts-${label.includes("Her") ? "her" : "him"}`}>
            <img src={img} alt={label} loading="lazy" style={{ objectPosition: objectPosition(img, config) }}
              className="h-full w-2/5 object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="flex-1 flex flex-col justify-center p-6">
              <p className="font-heading text-2xl font-semibold text-white">{label}</p>
              <p className="text-gold text-xs font-medium uppercase tracking-[0.2em] mt-1">Shop the edit →</p>
            </div>
          </Link>
        ))}
      </section>

      {/* 10 — Bestsellers rail, tabbed */}
      {showSection("bestsellers") && <Bestsellers store={store} />}

      {/* 10a — Shoppable videos (watch & shop) */}
      {hpSections?.shoppable_videos && <ShoppableVideos data={hpSections.shoppable_videos} />}

      {/* 10b — Lookbook with shoppable hotspots */}
      {hpSections?.lookbook && <LookbookHotspots data={hpSections.lookbook} />}

      {/* 10b — Deal of the Day (deepest live coupon × product) */}
      <DealOfTheDay store={store} />

      {/* 11 — Shop by Bond */}
      {showSection("gifting") && (
        <section className="container-x mt-16" data-testid="shop-by-bond">
          <SectionHead eyebrow="GIFTING" title={secTitle(config, "gifting", "Who are you buying for?")} link="/gifts" linkLabel="Open the gift finder" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            {BONDS.map(([label, key, img]) => (
              <Link key={key} to={`/gifts/${key}`} className="group" data-testid={`bond-${key}`}>
                <div className="aspect-square rounded-xl overflow-hidden border border-brand/10 bg-white relative">
                  {img
                    ? <>
                        <img src={img} alt={`For ${label}`} loading="lazy" style={{ objectPosition: objectPosition(img, config) }} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-brand/70 via-brand/10 to-transparent" />
                        <p className="absolute bottom-3 left-3 font-heading text-base font-semibold text-white">For {label}</p>
                      </>
                    : <div className="h-full w-full bg-brand-light flex items-center justify-center"><Heart size={22} className="text-brand-3" /></div>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* 12 — Personalised */}
      {showSection("personalised") && <PersonalisedBlock />}

      {/* 13 — Coins feature block */}
      {showSection("coins") && <CoinBlock />}

      {/* 13b — Bundle builder with quantity tiers */}
      {hpSections?.bundle_builder && <BundleBuilder data={hpSections.bundle_builder} />}

      {/* 14 — Gifting Made Easy (price bands) */}
      <section className="container-x mt-16" data-testid="price-bands">
        <SectionHead eyebrow="LUXURY WITHIN REACH" title={secTitle(config, "price_bands", "Beautiful things, every budget")} link="/gifts" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {PRICE_BANDS.map(([label, to, min, max]) => (
            <PriceBandTile key={label} label={label} to={to} min={min} max={max} />
          ))}
        </div>
      </section>

      {/* 14b — Tiered coupon cards */}
      <OfferCards />

      {/* 15 — New arrivals (category cards) */}
      <section className="container-x mt-16" data-testid="new-arrivals">
        <SectionHead eyebrow="FRESH IN" title={secTitle(config, "new_arrivals", "Just in")} link={`/shop?${store === "coins" ? "product_type=coin" : `metal=${store}`}&sort=newest`} />
        <Rail items={newArrivals.items} loaded={newArrivals.loaded} />
      </section>

      {/* 16 — Shop by Occasion */}
      <section className="container-x mt-16" data-testid="shop-by-occasion">
        <SectionHead eyebrow="THE OCCASION" title={secTitle(config, "occasions", "For the moment")} />
        <div className="flex gap-4 overflow-x-auto mt-6 pb-2" style={{ scrollbarWidth: "none" }}>
          {OCCASIONS.map(([label, to, occKey, tileKey]) => {
            const img = config?.category_tiles?.[occKey] || config?.category_tiles?.[tileKey];
            return (
            <Link key={label} to={to} className="relative shrink-0 w-52 h-64 rounded-xl overflow-hidden group border border-brand/10 bg-white" data-testid={`occasion-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              {img ? (
                <img src={img} alt={label} loading="lazy"
                  style={{ objectPosition: objectPosition(img, config) }}
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              ) : <div className="absolute inset-0 bg-brand-light" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
              <p className="absolute bottom-4 left-4 font-heading text-lg font-semibold text-white">{label}</p>
            </Link>
            );
          })}
        </div>
      </section>

      {/* 17 — Latest collections */}
      <LatestCollections />

      {/* 18 — Pia editorial band: 21:9 campaign frame, subject right, copy left */}
      <section className="mt-16 relative overflow-hidden" data-testid="pia-band">
        <div className="relative h-[52vh] min-h-[360px]">
          {config?.brand_imagery?.editorial_band ? (
            <img src={config.brand_imagery.editorial_band} alt="Meet Pia — the Siyaara campaign"
              className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: objectPosition(config.brand_imagery.editorial_band, config) }}
              data-testid="pia-band-image" />
          ) : (
            <video className="absolute inset-0 h-full w-full object-cover" src="/assets/pia/pia-intro.mp4"
              poster="/assets/pia/pia-intro-poster.jpg" autoPlay muted loop playsInline />
          )}
          <div className="absolute inset-0 flex items-center">
            <div className="absolute inset-y-0 left-0 w-full sm:w-2/3 bg-gradient-to-r from-white/90 via-white/65 to-transparent pointer-events-none" />
            <div className="container-x relative">
              {config?.brand_imagery?.editorial_band ? (
                <>
                  <p className="eyebrow !text-brand">THE FACE OF SIYAARA</p>
                  <h2 className="font-heading text-4xl sm:text-5xl font-semibold text-brand mt-2">Meet Pia</h2>
                  <p className="text-stone-700 text-sm mt-2 max-w-md">Warm neutrals, soft daylight, real jewellery. The campaign that wears like your day does.</p>
                  <Link to="/pia"><Button className="mt-5 bg-brand text-ivory hover:bg-brand-2 rounded-full px-7 btn-press" data-testid="pia-band-cta">Explore the campaign</Button></Link>
                </>
              ) : (
                <>
                  <p className="eyebrow !text-gold">THE FACE OF SIYAARA</p>
                  <h2 className="font-heading text-4xl sm:text-5xl font-semibold text-white mt-2">Meet Pia</h2>
                  <p className="text-white/80 text-sm mt-2 max-w-md">Warm neutrals, soft daylight, real jewellery. The campaign that wears like your day does.</p>
                  <Link to="/pia"><Button className="mt-5 bg-white/15 backdrop-blur border border-white/40 text-white hover:bg-white/25 rounded-full px-7 btn-press" data-testid="pia-band-cta">Explore the campaign</Button></Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 18b — Heritage band */}
      <HeritageBand />

      {/* 18c — Image compare (wax → gold craft story) */}
      {hpSections?.image_compare && <ImageCompare data={hpSections.image_compare} />}

      {/* 19 — Testimonials (real records only) */}
      {showSection("testimonials") && <SocialProof />}

      {/* 20 — Instagram strip */}
      <InstagramStrip />

      {/* 21 — FAQ accordion */}
      <section className="container-x mt-16 max-w-3xl" data-testid="home-faq">
        <SectionHead eyebrow="GOOD TO KNOW" title="Questions, answered" link="/faq" linkLabel="All FAQs" />
        <Accordion type="single" collapsible className="mt-6">
          {HOME_FAQS.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-brand/10">
              <AccordionTrigger className="text-left text-sm font-medium" data-testid={`home-faq-q-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-sm text-ink-2 leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* 21b — Style quiz: answers feed CRM affinity + preferences */}
      <StyleQuiz />

      {/* 22 — Newsletter */}
      {showSection("newsletter") && <Newsletter />}

      <div className="h-16 md:h-0" />
    </div>
  );
}
